import { definition, styles, context } from "@uesio/ui"
import { nanoid } from "@reduxjs/toolkit"
import Tile from "../tile/tile"
import UploadArea from "../uploadarea/uploadarea"
import { useRef } from "react"
import MenuButton from "../menubutton/menubutton"

type MenuItem = {
  id: string
  label: string
}

type FileInfo = {
  url: string
  path?: string
  mimetype?: string
}

export interface FileUtilityProps {
  id?: string
  mode?: context.FieldMode
  fileInfo?: FileInfo
  onUpload?: (files: FileList | null) => void
  onDelete?: () => void
  onPreview?: () => void
  onDownload?: () => void
  accept?: string
  readonly?: boolean
  showTile?: boolean
}

export const StyleDefaults = Object.freeze({
  root: [],
  filewrapper: [],
  input: [],
  readonly: [],
  selecteditemwrapper: [],
  selectediteminner: [],
  editbutton: [],
  menubutton: [],
  uploadarea: [],
  uploadareaactive: [],
  emptystate: [],
})

const File: definition.UtilityComponent<FileUtilityProps> = (props) => {
  const {
    context,
    fileInfo,
    onUpload,
    onDelete,
    onPreview,
    onDownload,
    accept,
    mode,
    readonly,
    showTile = true,
    children,
  } = props

  // uesio/io.filefield is used here even though File component is used by more than "filefield". For now, expanding on this to expose
  // support for FileImage, however the css via variants for File & File* components should be discrete and/or exposed another way.
  // TODO: Refactor variants to avoid using uesio/io.filefield for all File related components
  const classes = styles.useUtilityStyleTokens(
    StyleDefaults,
    props,
    "uesio/io.filefield",
  )

  const uploadLabelId = nanoid()
  const isEditMode = !readonly && mode === "EDIT"
  const fileInputRef = useRef<HTMLInputElement>(null)

  const menuItems: MenuItem[] = [
    ...(onPreview
      ? [
          {
            id: "preview",
            label: "Preview",
          },
        ]
      : []),
    ...(onDownload
      ? [
          {
            id: "download",
            label: "Download",
          },
        ]
      : []),
    ...(isEditMode && onUpload
      ? [
          {
            id: "upload",
            label: fileInfo ? "Replace" : "Upload",
          },
        ]
      : []),
    ...(isEditMode && onDelete
      ? [
          {
            id: "delete",
            label: "Delete",
          },
        ]
      : []),
  ]

  return (
    <div className={classes.filewrapper}>
      {isEditMode && onUpload ? (
        <UploadArea
          onUpload={onUpload}
          context={context}
          accept={accept}
          className={styles.cx(classes.uploadarea, classes.emptystate)}
          activeClassName={classes.uploadareaactive}
          uploadLabelId={uploadLabelId}
          fileInputRef={fileInputRef}
          onClick={() => {
            fileInputRef.current?.click()
          }}
        >
          {children}
          <div>
            Click or drag a file here to {fileInfo ? "replace" : "upload"}.
          </div>
        </UploadArea>
      ) : (
        children
      )}

      {showTile && (
        <Tile
          context={context}
          className={styles.cx(classes.root, classes.input, classes.readonly)}
        >
          {fileInfo?.url && (
            <div className={classes.selecteditemwrapper}>
              <div className={classes.selectediteminner}>{fileInfo.path}</div>
              <MenuButton
                buttonVariant="uesio/appkit.navicon"
                className={classes.menubutton}
                itemRenderer={(item: MenuItem) => item.label}
                items={menuItems}
                getItemKey={(item: MenuItem) => item.id}
                icon="more_vert"
                context={context}
                defaultPlacement="bottom-end"
                onSelect={(item: MenuItem) => {
                  switch (item.id) {
                    case "preview":
                      onPreview?.()
                      break
                    case "download":
                      onDownload?.()
                      break
                    case "upload":
                      fileInputRef.current?.click()
                      break
                    case "delete":
                      onDelete?.()
                      break
                  }
                }}
              />
            </div>
          )}
        </Tile>
      )}
    </div>
  )
}

export type { FileInfo }

export default File
